import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrder } from '../context/OrderContext';
import './PriceConfigPage.css';

const PriceConfigPage = () => {
  const navigate = useNavigate();
  const { articles, updatePrice, getArticlesByCategory } = useOrder();
  const [activeCategory, setActiveCategory] = useState('bebidas');
  const [tempPrices, setTempPrices] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Initialize temp prices with current prices
    const initialPrices = {};
    ['bebidas', 'comidas'].forEach(category => {
      getArticlesByCategory(category).forEach(article => {
        initialPrices[article.id] = article.price;
      });
    });
    setTempPrices(initialPrices);
  }, [articles, getArticlesByCategory]);

  const handlePriceChange = (articleId, newPrice) => {
    const price = parseFloat(newPrice);
    if (!isNaN(price) && price >= 0) {
      setTempPrices(prev => ({
        ...prev,
        [articleId]: price
      }));
      setHasChanges(true);
    }
  };

  const handleSavePrices = () => {
    ['bebidas', 'comidas'].forEach(category => {
      getArticlesByCategory(category).forEach(article => {
        const newPrice = tempPrices[article.id];
        if (newPrice !== undefined && newPrice !== article.price) {
          updatePrice(category, article.id, newPrice);
        }
      });
    });
    setHasChanges(false);
    alert('Precios actualizados exitosamente');
  };

  const handleResetPrices = () => {
    // Reset to original prices
    const originalPrices = {};
    ['bebidas', 'comidas'].forEach(category => {
      getArticlesByCategory(category).forEach(article => {
        originalPrices[article.id] = article.price;
      });
    });
    setTempPrices(originalPrices);
    setHasChanges(false);
  };

  const handleExportConfig = () => {
    const config = {
      bebidas: getArticlesByCategory('bebidas').map(article => ({
        id: article.id,
        name: article.name,
        price: tempPrices[article.id] || article.price
      })),
      comidas: getArticlesByCategory('comidas').map(article => ({
        id: article.id,
        name: article.name,
        price: tempPrices[article.id] || article.price
      }))
    };

    const dataStr = JSON.stringify(config, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = 'price_config.json';
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleImportConfig = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target.result);
          const newPrices = {};
          
          ['bebidas', 'comidas'].forEach(category => {
            if (config[category]) {
              config[category].forEach(article => {
                newPrices[article.id] = article.price;
              });
            }
          });
          
          setTempPrices(newPrices);
          setHasChanges(true);
          alert('Configuración importada exitosamente. Haz clic en "Guardar Precios" para aplicar los cambios.');
        } catch (error) {
          alert('Error al importar el archivo. Verifica el formato JSON.');
        }
      };
      reader.readAsText(file);
    }
  };

  const currentArticles = getArticlesByCategory(activeCategory);

  return (
    <div className="price-config-page">
      <div className="header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Volver
        </button>
        <h1>Configuración de Precios</h1>
      </div>
      
      <div className="main-content">
        <div className="category-tabs">
          <button 
            className={`tab-button ${activeCategory === 'bebidas' ? 'active' : ''}`}
            onClick={() => setActiveCategory('bebidas')}
          >
            🍹 Bebidas
          </button>
          <button 
            className={`tab-button ${activeCategory === 'comidas' ? 'active' : ''}`}
            onClick={() => setActiveCategory('comidas')}
          >
            🍕 Comidas
          </button>
        </div>

        <div className="price-list">
          {currentArticles.map((article) => (
            <div key={article.id} className="price-item">
              <div className="article-info">
                <span className="article-name">{article.name}</span>
                <span className="article-id">ID: {article.id}</span>
              </div>
              <div className="price-input-group">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  className="price-input"
                  value={tempPrices[article.id] || article.price}
                  onChange={(e) => handlePriceChange(article.id, e.target.value)}
                  step="0.01"
                  min="0"
                />
                <span className="current-price">
                  Actual: ${article.price.toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="action-buttons">
          <button className="action-button secondary" onClick={handleResetPrices}>
            🔄 Restablecer
          </button>
          
          <div className="import-export-group">
            <label className="action-button secondary">
              📥 Importar Configuración
              <input
                type="file"
                accept=".json"
                onChange={handleImportConfig}
                style={{ display: 'none' }}
              />
            </label>
            
            <button className="action-button secondary" onClick={handleExportConfig}>
              📤 Exportar Configuración
            </button>
          </div>
          
          <button 
            className={`action-button ${hasChanges ? 'primary' : 'disabled'}`}
            onClick={handleSavePrices}
            disabled={!hasChanges}
          >
            💾 Guardar Precios
          </button>
        </div>

        {hasChanges && (
          <div className="changes-warning">
            ⚠️ Tienes cambios sin guardar. Haz clic en "Guardar Precios" para aplicar los cambios.
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceConfigPage;
